import { Box } from '@mui/material';
import {
  BaseWidget,
  BaseWidgetProps,
  Config,
  FactoryWithContext,
} from '@react-awesome-query-builder/mui';

import {
  ElectionRecordResult,
  isTemplatePlaceholder,
  RosterCriteria,
  TemplatePlaceholder,
} from '@/types';
import { RosterTypeRecordResult } from '@/constants/filterCriteria/roster';
import { ElectorateRecordResult } from '@/types/filterCriteriaRecords';
import Roster from '@pages/QuickFilterListCreatorPage/criteriaComponents/Roster';
import { MappingError } from '@/pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/FilterMappers';

// This gets mapped to a RosterFilter
type RosterWidgetValue = {
  election: ElectionRecordResult | null | TemplatePlaceholder;
  electorates: ElectorateRecordResult[] | TemplatePlaceholder;
  rosterTypes: RosterTypeRecordResult[] | TemplatePlaceholder;
  rosterCriteria: RosterCriteria;
  errors: MappingError[];
};

type CombinedProps = BaseWidgetProps<Config, RosterWidgetValue>;

const DEFAULT_ROSTER_CRITERIA = { shiftStatus: 'Confirmed', votingPeriod: 'Any' };
const DEFAULT_ROSTER_TYPES = [{ id: 1, label: 'Rostered' }];

const RosterWidgetFactory: FactoryWithContext<CombinedProps> = ({
  value,
  setValue,
}: CombinedProps) => {
  const handleChangeElection = (newValue: ElectionRecordResult | null | TemplatePlaceholder) => {
    // setValue does not support updater functions like useState hook setter
    // function does, so we have to reference the old value directly.
    //
    // [LIST-1031] In the quick search context, clearing the election leads to
    // all fields being reset. In the advanced search context we instead retain
    // rosterCriteria and rosterTypes. We still reset/clear electorates, since
    // that depends on the election.
    //
    // The only exception is if it is a placeholder, it should remain so
    setValue({
      election: newValue,
      electorates: isTemplatePlaceholder(value?.electorates) ? value.electorates : [],
      rosterCriteria: value?.rosterCriteria ?? { ...DEFAULT_ROSTER_CRITERIA },
      rosterTypes: value?.rosterTypes ?? [...DEFAULT_ROSTER_TYPES],
      errors: [],
    });
  };

  const handleChangeElectorate = (newValue: ElectorateRecordResult[] | TemplatePlaceholder) => {
    // setValue does not support updater functions like useState hook setter
    // function does, so we have to reference the old value directly.
    if (value) setValue({ ...value, electorates: newValue, errors: [] });
  };

  const handleChangeRosterCriteria = (newValue: RosterCriteria) => {
    // setValue does not support updater functions like useState hook setter
    // function does, so we have to reference the old value directly.
    if (value) setValue({ ...value, rosterCriteria: newValue, errors: [] });
  };

  const handleChangeRosterType = (newValue: RosterTypeRecordResult[] | TemplatePlaceholder) => {
    // setValue does not support updater functions like useState hook setter
    // function does, so we have to reference the old value directly.

    if (value) {
      if (
        isTemplatePlaceholder(value.rosterTypes) &&
        !isTemplatePlaceholder(newValue) &&
        newValue.length == 0
      ) {
        setValue({
          ...value,
          rosterTypes: [
            {
              id: 1,
              label: 'Rostered',
            },
          ],
          errors: [],
        });
      } else {
        setValue({ ...value, rosterTypes: newValue, errors: [] });
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: "column",
        gap: 3,
        width: '50ch'
      }}>
      <Roster
        electionValue={value?.election ?? null}
        electorateValue={value?.electorates ?? []}
        rosterCriteriaValue={value?.rosterCriteria ?? {}}
        rosterTypeValue={value?.rosterTypes ?? []}
        electionOnChange={handleChangeElection}
        electorateOnChange={handleChangeElectorate}
        rosterCriteriaOnChange={handleChangeRosterCriteria}
        rosterTypeOnChange={handleChangeRosterType}
      />
    </Box>
  );
};

export const rosterWidget: BaseWidget = {
  type: 'roster',
  hideOperator: true,
  operatorInlineLabel: 'has',
  factory: RosterWidgetFactory,
};
